import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";
import { isPreviewHost } from "@/lib/env";
import { Wrench, ArrowRight } from "@phosphor-icons/react";

const BG_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a606c0a2-df34-4116-9cbf-9805a3fed300/images/fef5681fbf794a89d327cf1de0e57397b69ae7d488bb46c067a28c6e6bceb994.png";

export default function Login() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (ready && user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await login(email, password);
      navigate("/");
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const fillDemo = (e, p) => { setEmail(e); setPassword(p); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: image */}
      <div className="hidden lg:flex relative overflow-hidden border-r border-slate-200">
        <img src={BG_IMG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-white/0" />
        <div className="relative z-10 flex flex-col justify-between p-10 w-full text-slate-900">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-sm flex items-center justify-center" style={{ background: "#002FA7" }}>
              <Wrench size={18} weight="bold" color="#fff" />
            </div>
            <div className="font-heading font-semibold tracking-tight">ServinPri</div>
          </div>
          <div>
            <div className="overline mb-3">Repair & Maintenance Management</div>
            <h1 className="text-4xl font-heading font-semibold tracking-tight leading-[1.05] text-slate-900 max-w-md">
              Every repair, every store —<br/>one workflow.
            </h1>
            <p className="mt-4 text-sm text-slate-700 max-w-sm">
              Approvals, technician allocation, and spend tracking across all your sites.
              No more email chains.
            </p>
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-600">v1.2 · GBP · United Kingdom</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="overline mb-2">Sign in</div>
          <h2 className="text-2xl font-heading font-semibold tracking-tight mb-1">Welcome back</h2>
          <p className="text-sm text-slate-600 mb-8">Sign in to manage repair jobs across your estate.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="block text-xs font-heading uppercase tracking-wider text-slate-500 mb-1.5">Email</label>
              <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rmms-input" />
            </div>
            <div>
              <label className="block text-xs font-heading uppercase tracking-wider text-slate-500 mb-1.5">Password</label>
              <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rmms-input" />
            </div>
            {err && <div data-testid="login-error" className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}
            <button data-testid="login-submit-button" type="submit" disabled={busy}
              className="w-full h-11 rounded-md text-white font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
              style={{ background: "#002FA7" }}>
              {busy ? "Signing in…" : <>Sign in <ArrowRight size={16} weight="bold" /></>}
            </button>
          </form>

          {isPreviewHost() && (
          <div className="mt-8 border-t border-slate-200 pt-5">
            <div className="overline mb-3">Demo accounts</div>
            <div className="grid grid-cols-1 gap-1.5">
              {[
                ["admin@rmms.uk", "admin123", "Admin"],
                ["gm@rmms.uk", "demo123", "General Manager"],
                ["mm@rmms.uk", "demo123", "Maintenance Manager"],
                ["dm@rmms.uk", "demo123", "District Manager (£5k)"],
                ["am@rmms.uk", "demo123", "Area Manager"],
                ["sm@rmms.uk", "demo123", "Store Manager (£500)"],
                ["staff@rmms.uk", "demo123", "Store Staff"],
                ["tech@rmms.uk", "demo123", "Maintenance Staff"],
                ["contractor@rmms.uk", "demo123", "Contractor"],
              ].map(([e, p, l]) => (
                <button key={e} type="button" onClick={() => fillDemo(e, p)}
                  data-testid={`demo-login-${e.split("@")[0]}`}
                  className="text-left text-xs px-3 py-2 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors flex items-center justify-between">
                  <span className="font-mono-data">{e}</span>
                  <span className="text-slate-500">{l}</span>
                </button>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
